import { _ } from '@ngx-translate/core';

/** Why the upload endpoint refused a file. `other` covers server faults and anything it does not name. */
export type AttachmentUploadRejection = 'too_large' | 'banned_type' | 'dangerous' | 'other';

// The api answers both with a 422, so only its feedback line tells them apart.
const TOO_LARGE_FEEDBACK = "File can't be larger than 5MB";
const BANNED_TYPE_FEEDBACK = 'File type is banned';

export const attachmentUploadRejection = (status: number, response: unknown): AttachmentUploadRejection => {
  const feedback = extractFeedback(response);

  if (feedback.includes(TOO_LARGE_FEEDBACK) || status === 413) {
    return 'too_large';
  }

  if (feedback.includes(BANNED_TYPE_FEEDBACK) || status === 422) {
    return 'banned_type';
  }

  return status === 400 ? 'dangerous' : 'other';
};

export const attachmentUploadError = (status: number, filter: any): string => {
  switch (attachmentUploadRejection(status, filter)) {
    case 'too_large':
      return _('The file is too large');

    case 'banned_type':
      return _('You are not allowed to upload files of this type');

    case 'dangerous':
      return _('The file you uploaded may be dangerous and cannot be uploaded to Shiftbase');

    default:
      // What the copy said before the feedback was read: a transport failure is usually the body limit.
      return _('The file is too large');
  }
};

/**
 * The api sends feedback as `{ message, level, field? }` entries; only the message is compared.
 * The file uploader hands the body over as text, `HttpClient` hands it over parsed.
 */
const extractFeedback = (response: unknown): string[] => {
  const body = typeof response === 'string' ? parseJson(response) : response;

  if (typeof body !== 'object' || body === null || !('meta' in body)) {
    return [];
  }

  const { meta } = body;

  if (typeof meta !== 'object' || meta === null || !('feedback' in meta)) {
    return [];
  }

  const { feedback } = meta;

  return Array.isArray(feedback) ? feedback.map(feedbackMessage).filter((line) => line !== undefined) : [];
};

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const feedbackMessage = (entry: unknown): string | undefined => {
  if (typeof entry === 'string') {
    return entry;
  }

  if (typeof entry === 'object' && entry !== null && 'message' in entry && typeof entry.message === 'string') {
    return entry.message;
  }

  return undefined;
};

const isUploadResponse = (body: unknown): body is { data: { resource_id: string } } => {
  if (typeof body !== 'object' || body === null || !('data' in body)) {
    return false;
  }

  const { data } = body;

  if (typeof data !== 'object' || data === null || !('resource_id' in data)) {
    return false;
  }

  // A truthy check would pass a number or an object straight through as the resource id.
  return typeof data.resource_id === 'string' && data.resource_id.length > 0;
};

export const readUploadedResourceId = (response: string): string | undefined => {
  try {
    const body: unknown = JSON.parse(response);

    return isUploadResponse(body) ? body.data.resource_id : undefined;
  } catch {
    return undefined;
  }
};
